import { useCallback, useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import WorkspaceGrid from "@/components/WorkspaceGrid";
import WidgetLibrary from "@/components/WidgetLibrary";
import DrilldownModal from "@/components/DrilldownModal";
import ShareDialog from "@/components/ShareDialog";
import TemplatePicker from "@/components/TemplatePicker";
import api from "@/lib/api";
import { useAccounts } from "@/hooks/useData";
import { widgetMeta } from "@/lib/widgets";
import { RANGE_OPTIONS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronDown, Plus, Share2, Pencil, Check, LayoutGrid, Trash2, PackagePlus, FileDown, Loader2 } from "lucide-react";

function newId() {
  return `wg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export default function WorkspacePage() {
  const { data: accounts = [] } = useAccounts();
  const [workspaces, setWorkspaces] = useState(null);
  const [currentId, setCurrentId] = useState(() => localStorage.getItem("mytradingroom-ws") || null);
  const [items, setItems] = useState([]);
  const [wsSettings, setWsSettings] = useState({ account_id: "demo-scalper", range: "all" });
  const [editMode, setEditMode] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [droppingType, setDroppingType] = useState(null);
  const [drillItem, setDrillItem] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [newWsOpen, setNewWsOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const saveTimer = useRef(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const current = workspaces?.find((w) => w.workspace_id === currentId);

  const loadWorkspaces = useCallback(async () => {
    const { data } = await api.get("/workspaces");
    setWorkspaces(data);
    setCurrentId((prev) => {
      const valid = data.find((w) => w.workspace_id === prev);
      const next = valid ? prev : data[0]?.workspace_id || null;
      if (next) localStorage.setItem("mytradingroom-ws", next);
      return next;
    });
  }, []);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  useEffect(() => {
    if (!current) return;
    setItems(current.layout || []);
    setWsSettings({ account_id: "demo-scalper", range: "all", ...(current.settings || {}) });
  }, [currentId, workspaces]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistLayout = useCallback((nextItems) => {
    if (!currentId) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(`/workspaces/${currentId}`, { layout: nextItems });
      } catch {
        toast.error("Couldn't save layout");
      }
    }, 700);
  }, [currentId]);

  const onLayoutChange = useCallback((layout) => {
    const next = itemsRef.current.map((it) => {
      const l = layout.find((x) => x.i === it.i);
      return l ? { ...it, x: l.x, y: l.y, w: l.w, h: l.h } : it;
    });
    const changed = JSON.stringify(next.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }))) !==
      JSON.stringify(itemsRef.current.map(({ i, x, y, w, h }) => ({ i, x, y, w, h })));
    if (changed) {
      setItems(next);
      persistLayout(next);
    }
  }, [persistLayout]);

  const addWidget = useCallback((type, x = null, y = null) => {
    const meta = widgetMeta(type);
    const bottom = Math.max(0, ...itemsRef.current.map((it) => it.y + it.h));
    const item = {
      i: newId(), widget_type: type,
      x: x ?? 0, y: y ?? bottom,
      w: meta.w, h: meta.h, settings: {},
    };
    const next = [...itemsRef.current, item];
    setItems(next);
    persistLayout(next);
    toast.success(`${meta.name} added`);
  }, [persistLayout]);

  const removeWidget = useCallback((i) => {
    const next = itemsRef.current.filter((it) => it.i !== i);
    setItems(next);
    persistLayout(next);
  }, [persistLayout]);

  const updateWidgetSettings = useCallback((i, settings) => {
    const next = itemsRef.current.map((it) => (it.i === i ? { ...it, settings } : it));
    setItems(next);
    persistLayout(next);
  }, [persistLayout]);

  const updateWsSettings = async (patch) => {
    const next = { ...wsSettings, ...patch };
    setWsSettings(next);
    if (currentId) {
      try {
        await api.put(`/workspaces/${currentId}`, { settings: next });
      } catch { /* silent */ }
    }
  };

  const createWorkspace = async (name, templateId) => {
    setCreating(true);
    try {
      const { data } = await api.post("/workspaces", { name, template_id: templateId });
      setWorkspaces((prev) => [...(prev || []), data]);
      setCurrentId(data.workspace_id);
      localStorage.setItem("mytradingroom-ws", data.workspace_id);
      setNewWsOpen(false);
      toast.success(`Workspace “${data.name}” created`);
      if (!templateId) {
        setEditMode(true);
        setLibraryOpen(true);
      }
    } catch (err) {
      toast.error("Couldn't create workspace");
    } finally {
      setCreating(false);
    }
  };

  const renameWorkspace = async () => {
    const { data } = await api.put(`/workspaces/${currentId}`, { name: renameValue });
    setWorkspaces((prev) => prev.map((w) => (w.workspace_id === currentId ? data : w)));
    setRenameOpen(false);
    toast.success("Workspace renamed");
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const { data } = await api.get(`/workspaces/${currentId}/report`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      const slug = (current?.name || "workspace").replace(/[^a-zA-Z0-9]+/g, "-");
      a.href = url;
      a.download = `MyTradingRoom_${slug}_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch {
      toast.error("Couldn't generate the report. Try again.");
    } finally {
      setExporting(false);
    }
  };

  const deleteWorkspace = async () => {
    await api.delete(`/workspaces/${currentId}`);
    const rest = workspaces.filter((w) => w.workspace_id !== currentId);
    setWorkspaces(rest);
    const next = rest[0]?.workspace_id || null;
    setCurrentId(next);
    if (next) localStorage.setItem("mytradingroom-ws", next);
    else localStorage.removeItem("mytradingroom-ws");
    setDeleteOpen(false);
    toast.success("Workspace deleted");
  };

  // ------- render -------
  if (workspaces === null) {
    return (
      <AppShell>
        <div className="space-y-4 p-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-4 gap-3">
            {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </AppShell>
    );
  }

  if (!workspaces.length) {
    return (
      <AppShell>
        <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:py-14" data-testid="workspace-empty-state">
          <h1 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">Build your first workspace</h1>
          <p className="mt-1.5 max-w-xl text-sm text-muted-foreground">
            Pick a template built for how you trade, or start from a blank canvas.
            Every widget can be moved, resized or removed afterwards.
          </p>
          <div className="mt-6">
            <TemplatePicker onCreate={createWorkspace} creating={creating} />
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          {/* toolbar */}
          <div className="flex flex-wrap items-center gap-2 border-b bg-card/60 px-3 py-2 sm:px-4 lg:px-6">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-9 gap-1.5 px-2.5 font-display text-base font-semibold" data-testid="workspace-switcher">
                  {current?.name}
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-60">
                {workspaces.map((w) => (
                  <DropdownMenuItem key={w.workspace_id}
                    onClick={() => { setCurrentId(w.workspace_id); localStorage.setItem("mytradingroom-ws", w.workspace_id); }}
                    data-testid={`workspace-option-${w.workspace_id}`}>
                    <LayoutGrid className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 truncate">{w.name}</span>
                    {w.workspace_id === currentId && <Check className="h-4 w-4 text-primary" />}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setNewWsOpen(true)} data-testid="new-workspace-button">
                  <Plus className="mr-2 h-4 w-4" /> New workspace
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setRenameValue(current?.name || ""); setRenameOpen(true); }} data-testid="rename-workspace-button">
                  <Pencil className="mr-2 h-4 w-4" /> Rename
                </DropdownMenuItem>
                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setDeleteOpen(true)} data-testid="delete-workspace-button">
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Select value={wsSettings.account_id} onValueChange={(v) => updateWsSettings({ account_id: v })}>
                <SelectTrigger className="h-9 w-[190px] text-xs" data-testid="workspace-account-select">
                  <SelectValue placeholder="Account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.filter((a) => a.status !== "pending").map((a) => (
                    <SelectItem key={a.account_id} value={a.account_id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={wsSettings.range} onValueChange={(v) => updateWsSettings({ range: v })}>
                <SelectTrigger className="h-9 w-[120px] text-xs" data-testid="workspace-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANGE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="secondary" className="h-9" onClick={() => { setLibraryOpen((o) => !o); if (!libraryOpen) setEditMode(true); }}
                data-testid="add-widgets-button">
                <PackagePlus className="mr-1.5 h-4 w-4" /> Widgets
              </Button>
              <Button variant={editMode ? "default" : "outline"} className="h-9"
                onClick={() => { setEditMode((e) => !e); if (editMode) { setLibraryOpen(false); toast.success("Layout saved"); } }}
                data-testid="edit-mode-toggle">
                {editMode ? <><Check className="mr-1.5 h-4 w-4" /> Done</> : <><Pencil className="mr-1.5 h-4 w-4" /> Edit</>}
              </Button>
              <Button variant="outline" className="h-9" onClick={exportPdf} disabled={exporting} data-testid="export-pdf-button">
                {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />} PDF
              </Button>
              <Button variant="outline" className="h-9" onClick={() => setShareOpen(true)} data-testid="share-workspace-button">
                <Share2 className="mr-1.5 h-4 w-4" /> Share
              </Button>
            </div>
          </div>

          {/* canvas */}
          <div className="min-h-0 flex-1 overflow-y-auto bg-surface-2 px-2 py-3 sm:px-3 lg:px-5">
            <div className="relative min-h-[400px]">
              {items.length === 0 && (
                <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 text-center" data-testid="canvas-empty-state">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                    <LayoutGrid className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-display text-lg font-semibold">This workspace is empty</h3>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    Open the widget library and drag your first widget onto the canvas, or press + on any widget card.
                  </p>
                  {!libraryOpen && (
                    <Button className="pointer-events-auto" onClick={() => { setEditMode(true); setLibraryOpen(true); }} data-testid="empty-open-library-button">
                      <PackagePlus className="mr-1.5 h-4 w-4" /> Open widget library
                    </Button>
                  )}
                </div>
              )}
              <WorkspaceGrid
                items={items}
                editMode={editMode}
                droppingType={droppingType}
                onLayoutChange={onLayoutChange}
                onDropWidget={(type, x, y) => { addWidget(type, x, y); setDroppingType(null); }}
                defaults={wsSettings}
                accounts={accounts}
                onRemove={removeWidget}
                onSettingsChange={updateWidgetSettings}
                onDrill={(item) => setDrillItem(item)}
              />
            </div>
          </div>
        </div>

        {libraryOpen && (
          <WidgetLibrary onAdd={(type) => addWidget(type)} onDragType={setDroppingType} onClose={() => setLibraryOpen(false)} />
        )}
      </div>

      <DrilldownModal item={drillItem} defaults={wsSettings} accounts={accounts} shareToken={null}
        open={!!drillItem} onClose={() => setDrillItem(null)} />
      <ShareDialog workspaceId={currentId} workspaceName={current?.name} open={shareOpen} onClose={() => setShareOpen(false)} />

      <Dialog open={newWsOpen} onOpenChange={setNewWsOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display">New workspace</DialogTitle>
            <DialogDescription>Pick a template or start from a blank canvas.</DialogDescription>
          </DialogHeader>
          <TemplatePicker onCreate={createWorkspace} creating={creating} />
        </DialogContent>
      </Dialog>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Rename workspace</DialogTitle>
            <DialogDescription>Give this workspace a new name.</DialogDescription>
          </DialogHeader>
          <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} data-testid="rename-input" />
          <Button onClick={renameWorkspace} data-testid="rename-confirm-button">Save</Button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{current?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the workspace and its share links. Your accounts and data are untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={deleteWorkspace} data-testid="delete-confirm-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
